import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, DoorOpen, Globe2, ImageIcon, Lock, LogOut, Plus, UserRound, Users } from 'lucide-react';
import {
  createRoom,
  fetchRooms,
  fetchVideos,
  getStreamUrl,
  joinRoom,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AvatarStack } from '../components/UserAvatar';
import type { Room, Video, Visibility } from '../types';

function videoLabel(video?: Video): string {
  if (!video) return 'Video unavailable';
  return video.visibility === 'private' ? `${video.title} (Private)` : video.title;
}

function visibilityLabel(visibility: Visibility): string {
  return visibility === 'private' ? 'Private' : 'Public';
}

function inviteUrl(room: Room): string {
  const path = room.invitePath || `/rooms/${room.id}?invite=1`;
  return `${window.location.origin}${path}`;
}

function roomPreviewUrl(room: Room): string | null {
  const video = room.currentVideo;
  if (!video?.thumbnailUrl) return null;
  return getStreamUrl(video.thumbnailUrl, video.visibility === 'private');
}

function parseRoomInput(value: string): string {
  const trimmed = value.trim();
  const match = trimmed.match(/\/rooms\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : trimmed;
}

export const RoomsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [rooms, setRooms] = useState<Room[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [roomName, setRoomName] = useState('');
  const [roomVisibility, setRoomVisibility] = useState<Visibility>('public');
  const [selectedVideoId, setSelectedVideoId] = useState('');
  const [joinRoomId, setJoinRoomId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const readyVideos = useMemo(
    () => videos.filter((video) => video.status === 'ready'),
    [videos]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [roomData, videoData] = await Promise.all([fetchRooms(), fetchVideos()]);
      setRooms(roomData);
      setVideos(videoData);
      setError(null);
    } catch {
      setError('Unable to load rooms.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedVideoId && readyVideos.length > 0) {
      setSelectedVideoId(readyVideos[0].id);
    }
  }, [readyVideos, selectedVideoId]);

  const handleCreate = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedVideoId) return;
    setSubmitting(true);
    setError(null);
    try {
      const room = await createRoom(
        selectedVideoId,
        roomName.trim() || 'Co-watching room',
        roomVisibility
      );
      navigate(`/rooms/${room.id}`);
    } catch {
      setError('Unable to create the room.');
    } finally {
      setSubmitting(false);
    }
  };

  const joinById = async (roomId: string) => {
    setSubmitting(true);
    setError(null);
    try {
      const room = await joinRoom(roomId);
      navigate(`/rooms/${room.id}`);
    } catch {
      setError('Unable to join that room.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoin = async (event: React.FormEvent) => {
    event.preventDefault();
    const roomId = parseRoomInput(joinRoomId);
    if (!roomId) return;
    await joinById(roomId);
  };

  const handleCopyInvite = async (room: Room) => {
    const link = inviteUrl(room);
    try {
      await navigator.clipboard.writeText(link);
      setError(null);
    } catch {
      setJoinRoomId(link);
      setError('Invite link is shown in the join field.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-6xl flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between sm:px-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="shrink-0 text-left text-lg font-black tracking-tight text-primary-foreground"
          >
            <span className="bg-foreground text-primary px-2 py-0.5 font-mono mr-1.5">ABR</span>
            streaming
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <span className="hidden sm:inline text-xs font-mono font-black text-primary-foreground">{user?.login}</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/upload')} className="text-primary-foreground hover:text-primary-foreground">
              <Plus className="w-4 h-4" />
              Upload
            </Button>
            <Button variant="ghost" size="sm" onClick={() => navigate('/account')} className="text-primary-foreground hover:text-primary-foreground">
              <UserRound className="w-4 h-4" />
              Account
            </Button>
            <Button variant="ghost" size="sm" onClick={logout} className="text-primary-foreground hover:text-primary-foreground">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-6 px-3 py-5 sm:space-y-8 sm:px-6 sm:py-8">
        <div className="flex items-start gap-3">
          <div className="border-2 border-foreground bg-primary p-3 shadow-neo-sm">
            <Users className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-black text-foreground sm:text-3xl">Co-watching rooms</h1>
            <p className="text-sm text-foreground/60 font-medium">
              Create a public or private room, invite by link, and watch in sync.
            </p>
          </div>
        </div>

        {error && (
          <p className="border-2 border-destructive bg-destructive text-destructive-foreground px-3 py-2 text-sm font-bold">
            {error}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardContent className="space-y-4 p-4 sm:p-5">
              <h2 className="font-black text-lg">Create room</h2>
              <form onSubmit={(event) => void handleCreate(event)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="room-name">Room name</Label>
                  <Input
                    id="room-name"
                    value={roomName}
                    onChange={(event) => setRoomName(event.target.value)}
                    placeholder="Friday watch session"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Visibility</Label>
                  <div className="grid grid-cols-1 gap-2 min-[380px]:grid-cols-2">
                    {(['public', 'private'] as const).map((option) => (
                      <button
                        key={option}
                        type="button"
                        onClick={() => setRoomVisibility(option)}
                        className={`border-2 border-foreground px-3 py-2 text-sm font-black shadow-neo-sm ${
                          roomVisibility === option ? 'bg-primary text-foreground' : 'bg-card text-foreground'
                        }`}
                      >
                        {option === 'public' ? 'Public' : 'Private'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="room-video">Video</Label>
                  <select
                    id="room-video"
                    value={selectedVideoId}
                    onChange={(event) => setSelectedVideoId(event.target.value)}
                    className="w-full h-10 border-2 border-foreground bg-background px-3 text-sm font-bold text-foreground shadow-neo-sm"
                  >
                    {readyVideos.map((video) => (
                      <option key={video.id} value={video.id}>{videoLabel(video)}</option>
                    ))}
                  </select>
                </div>
                <Button type="submit" className="w-full sm:w-auto" disabled={submitting || readyVideos.length === 0}>
                  <Plus className="w-4 h-4" />
                  Create
                </Button>
              </form>
              {readyVideos.length === 0 && (
                <p className="text-xs font-bold text-foreground/60">
                  Upload and process a video before creating a room.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 p-4 sm:p-5">
              <h2 className="font-black text-lg">Join room</h2>
              <form onSubmit={(event) => void handleJoin(event)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="join-room-id">Room ID or invite link</Label>
                  <Input
                    id="join-room-id"
                    value={joinRoomId}
                    onChange={(event) => setJoinRoomId(event.target.value)}
                    placeholder="Paste room id or invite link"
                  />
                </div>
                <Button type="submit" variant="secondary" className="w-full sm:w-auto" disabled={submitting || !joinRoomId.trim()}>
                  <DoorOpen className="w-4 h-4" />
                  Join
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <section className="space-y-4">
          <h2 className="inline-flex border-2 border-foreground bg-primary px-3 py-1 text-xl font-black text-primary-foreground shadow-neo-sm sm:text-2xl">
            Visible rooms
          </h2>

          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-8 h-8 border-2 border-t-transparent border-foreground rounded-full animate-spin" />
            </div>
          ) : rooms.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-sm font-bold text-foreground/60">
                No rooms yet.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {rooms.map((room) => {
                const previewUrl = roomPreviewUrl(room);
                const activeCount = room.activeParticipantCount ?? room.activeParticipants?.length ?? 0;

                return (
                  <Card key={room.id}>
                    <CardContent className="space-y-3 p-3 sm:space-y-4 sm:p-4">
                      <div className="relative aspect-video overflow-hidden border-2 border-foreground bg-muted">
                        {previewUrl ? (
                          <img
                            src={previewUrl}
                            alt={videoLabel(room.currentVideo)}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <div className="flex h-full flex-col items-center justify-center gap-2 text-foreground/60">
                            <ImageIcon className="h-8 w-8" />
                            <span className="text-xs font-mono font-black uppercase">
                              Preview unavailable
                            </span>
                          </div>
                        )}
                        <span className="absolute bottom-2 left-2 max-w-[calc(100%-1rem)] truncate border-2 border-foreground bg-background px-2 py-1 text-[10px] font-mono font-black uppercase shadow-neo-sm">
                          {room.currentVideo?.status ?? 'Hidden video'}
                        </span>
                      </div>

                      <div className="space-y-2">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 className="min-w-0 flex-1 truncate text-base font-black text-foreground sm:text-lg">{room.name}</h3>
                          <span className="inline-flex shrink-0 items-center gap-1 border-2 border-foreground bg-card px-2 py-0.5 text-[10px] font-mono font-black uppercase">
                            {room.visibility === 'private' ? <Lock className="w-3 h-3" /> : <Globe2 className="w-3 h-3" />}
                            {visibilityLabel(room.visibility)}
                          </span>
                        </div>
                        <p className="text-sm text-foreground/60 font-medium truncate">
                          {videoLabel(room.currentVideo)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-2 border-y-2 border-foreground py-2 text-xs font-mono font-black">
                        <span>Active {activeCount}</span>
                        <span>{room.isMember ? (room.isOwner ? 'Admin' : 'Joined') : 'Not joined'}</span>
                      </div>
                      {room.isMember && room.activeParticipants && room.activeParticipants.length > 0 && (
                        <AvatarStack
                          people={room.activeParticipants}
                        />
                      )}
                      <div className="flex gap-2">
                        <Button
                          className="min-w-0 flex-1"
                          disabled={submitting && !room.isMember}
                          onClick={() => {
                            if (room.isMember) {
                              navigate(`/rooms/${room.id}`);
                              return;
                            }
                            void joinById(room.id);
                          }}
                        >
                          {room.isMember ? 'Watch' : 'Join'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => void handleCopyInvite(room)}
                          title="Copy invite link"
                          aria-label="Copy invite link"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
};
